import React, { useEffect, useState } from "react";
import { FusionLayout } from "@/parts/fusion-layout";
import { store } from "../../store";
import { observer } from "mobx-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import type { DateRange } from "react-day-picker";
import { DashboardSafetyTab } from "@/parts/dashboard-safety-tab";
import { DashboardFleetStatusTab } from "@/parts/dashboard-fleet-status-tab";
import {
    type DateRangeQuickSelector,
    detectDateRangeQuickSelector,
    quickSelectorToDateRange,
} from "@/lib/time";
import { useLocalStorage } from "../../utils/hooks/use-local-storage";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";

enum Mode {
    FleetStatus = "fleetStatus",
    Safety = "safety",
    Productivity = "productivity",
    Maintenance = "maintenance",
}

export const PageDashboard = observer((): JSX.Element => {
    const user = store.serviceOwnUser.user;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [quickSelector, setQuickSelector] = useLocalStorage<DateRangeQuickSelector>(
        "dashboardDateRange",
        "thisMonth",
    );
    const [range, setRange] = useState<DateRange>(quickSelectorToDateRange(quickSelector));
    useEffect(() => {
        const newQuickSelector = range && detectDateRangeQuickSelector(range);
        if (newQuickSelector && newQuickSelector !== quickSelector) {
            setQuickSelector(newQuickSelector);
        }
    }, [quickSelector, range, setQuickSelector]);

    const [_, setDashboardIsLegacy] = useLocalStorage("legacyDashboard", false);

    return (
        <FusionLayout breadcrumb={t("page.dashboard.navbarEntry")} className="relative">
            <Button
                variant="secondary"
                className="absolute top-4 right-4"
                onClick={() => {
                    setDashboardIsLegacy(true);
                    navigate("/legacy-dashboard");
                }}
            >
                {t("page.dashboard.legacyDashboard")}
                <ArrowRight />
            </Button>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col justify-between gap-2 md:flex-row">
                    <h3 className="mt-8 scroll-m-20 text-3xl font-bold tracking-tight">
                        {`${t("page.dashboard.welcome")}, ${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
                    </h3>
                    <DateRangePicker className="self-end" range={range} onChange={setRange} />
                </div>
                <Tabs defaultValue="fleetStatus" className="flex flex-col gap-2">
                    <TabsList className="grid w-full grid-cols-4">
                        {Object.values(Mode).map((value) => (
                            <TabsTrigger
                                key={value}
                                value={value}
                                disabled={![Mode.FleetStatus, Mode.Safety].includes(value)}
                            >
                                {t(`page.dashboard.mode.${value}`)}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <TabsContent value="safety">
                        <DashboardSafetyTab range={range} />
                    </TabsContent>
                    <TabsContent value="fleetStatus">
                        <DashboardFleetStatusTab range={range} />
                    </TabsContent>
                </Tabs>
            </div>
        </FusionLayout>
    );
});
